import { useState, useEffect, useRef } from "react";
import SearchIcon from "../../assets/searchIcon.svg";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { CircularProgress } from "@mui/material";
import { ReportServices } from "../../services/ReportServices";
import { handleApiErrorResponses } from "../../utils/ApisErrorHandling";
import { getUser } from "../../config/user";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import HospitalCustomPaginaion from "../../utils/HospitalPaginationUtil";
import { toast } from "react-toastify";
import { filter } from "jszip";

const HsaMonthlyCaseReport = () => {
  const [reportData, setReportData] = useState([]);
  const [filteredReportData, setFilteredReportData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);

  const [stageType, setStageType] = useState("all");
  const [caseType, setCaseType] = useState("");
  const [caseStage, setCaseStage] = useState("");
  const [filterStage, setFilterStage] = useState("Request Admission Initiated");
  const [subStageType, setSubStageType] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [value, setValue] = useState(10);

  // New: year/month selection
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(""); // "01".."12"

  // Internal start/end dates actually sent to API
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [loading, setLoading] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [searchKey, setSearchKey] = useState("");

  const user = getUser();
  const searchTimeRef = useRef();

  const handleSearch = (e) => {
    if (searchTimeRef.current) clearInterval(searchTimeRef.current);
    searchTimeRef.current = setTimeout(
      () => setSearchKey(e.target.value.trim()),
      500,
    );
    setPage(1);
  };

  // Helper: set start/end for a whole month (UTC)
  const setMonthRange = (year, month) => {
    if (!year || !month) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    const y = Number(year);
    const m = Number(month) - 1; // JS Date month index
    const start = new Date(Date.UTC(y, m, 1, 0, 0, 0, 0));
    const end = new Date(Date.UTC(y, m + 1, 0, 23, 59, 59, 999));

    setStartDate(start.toISOString());
    setEndDate(end.toISOString());
  };

  // When year/month change, compute start/end; block current month
  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = String(now.getMonth() + 1).padStart(2, "0");

    if (
      String(selectedYear) === String(currentYear) &&
      selectedMonth === currentMonth
    ) {
      setStartDate(null);
      setEndDate(null);
      setReportData([]);
      setFilteredReportData([]);
      setPaginatedData([]);
      toast.error(
        "The selected month has not ended yet. Monthly data will be available after the month is complete.",
      );
      return;
    }

    setMonthRange(selectedYear, selectedMonth);
  }, [selectedYear, selectedMonth]);

  const getMonthlyReport = async () => {
    setLoading(true);
    try {
      const res = await ReportServices?.getHsaMonthlyReport(
        startDate,
        endDate,
        user?.hospital?.id,
        filterStage,
      );

      const data = res?.data?.monthlyCaseReport || [];
      setReportData(data);
      setFilteredReportData(data);
      setPage(1);

      const startIndex = 0;
      const sliced = data.slice(startIndex, startIndex + limit);
      setPaginatedData(sliced);
    } catch (error) {
      handleApiErrorResponses(error);
    }
    setLoading(false);
  };

  //   console.log("start date:", startDate);
  //   console.log("end date:", endDate);\
  const formatForDateInput = (date) => {
    if (!date.getFullYear) return date;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const generatePdf = (caseData) => {
    const doc = new jsPDF("landscape");

    const formatDate = (date) =>
      date
        ? new Date(date).toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          })
        : "NA";

    const headers = [
      "SNo.",
      "Patient Name",
      "Beneficiary ID",
      "Registered ID",
      "Gender/Age",
      "Registered Date",
      "Admission Date",
      // "Current Case Stage",
      //"Discharge Date",
      //"Claim Initiated Date",
      // "Case Type",
    ];

    const rows = caseData.map((item, index) => [
      index + 1,
      item.patientName || "NA",
      item.beneficiaryNo || "NA",
      item.pmjayRegistrationId || "NA",
      `${item.gender || "NA"}/${item.age || "NA"}`,
      formatDate(item.registeredDate),
      formatDate(item?.admissionStage[0]?.createdAt),
      //formatDate(item.dischargeInitiatedDate),
      //formatDate(item.claimInitiatedDate),
      // item.caseType || "NA",

      {
        content: item.currentStageHsaReportText || "NA",
        colSpan: 2,
        styles: { halign: "center" },
      },
    ]);

    autoTable(doc, {
      head: [[...headers, "Current Case Stage", ""]],
      body: rows,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
      },
      startY: 20,
    });

    doc.save("case_report.pdf");
  };

  const generateExcel = (caseData) => {
    if (!caseData?.length) {
      toast.info("No data present to generate excel.");
      return;
    }

    try {
      const excelData = caseData.map((item, index) => {
        const gender =
          item?.gender?.toLowerCase() === "male"
            ? "M"
            : item?.gender?.toLowerCase() === "female"
              ? "F"
              : item?.gender
                ? "O"
                : "NA";

        const admissionDate = item?.admissionStage?.[0]?.createdAt;

        const row = {
          "SNo.": index + 1,
          "Patient Name": item?.patientName || "NA",
          "Beneficiary ID": item?.beneficiaryNo || "NA",
          "Registered ID": item?.pmjayRegistrationId || "NA",
          "Gender/Age": `${gender}/${item?.age || "NA"}`,
          "Registered Date": item?.registeredDate
            ? new Date(item.registeredDate).toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
              })
            : "NA",
          "Admission Initiated Date": admissionDate
            ? new Date(admissionDate).toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
              })
            : "NA",
          "Current Case Stage": item?.currentStageHsaReportText || "NA",
        };

        if (filterStage?.includes("Paid")) {
          return {
            "SNo.": index + 1,
            "Patient Name": item?.patientName || "NA",
            "Beneficiary ID": item?.beneficiaryNo || "NA",
            "Registered ID": item?.pmjayRegistrationId || "NA",
            "Registered Date": item?.registeredDate
              ? new Date(item.registeredDate).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: false,
                })
              : "NA",
            "Admission Initiated Date": admissionDate
              ? new Date(admissionDate).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: false,
                })
              : "NA",
            "Claim Initiated Amount":
              item?.caseDetails?.claimInitiatedAmount || "NA",
            "Claim Approved Amount":
              item?.caseDetails?.claimApprovedAmount || "NA",
            "Claim Paid Amount": item?.caseDetails?.claimPaidAmount || "NA",
            TDS: item?.caseDetails?.tdsAmount || "NA",
            "Current Case Stage": item?.currentStageHsaReportText || "NA",
          };
        }

        return row;
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);

      worksheet["!cols"] = filterStage?.includes("Paid")
        ? [
            { wch: 8 },
            { wch: 25 },
            { wch: 22 },
            { wch: 22 },
            { wch: 22 },
            { wch: 25 },
            { wch: 22 },
            { wch: 22 },
            { wch: 22 },
            { wch: 15 },
            { wch: 30 },
          ]
        : [
            { wch: 8 },
            { wch: 25 },
            { wch: 22 },
            { wch: 22 },
            { wch: 15 },
            { wch: 25 },
            { wch: 25 },
            { wch: 30 },
          ];

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Case Report");

      const monthName =
        months.find((m) => m.value === selectedMonth)?.label || "Month";

      XLSX.writeFile(
        workbook,
        `HSA_Monthly_Case_Report_${monthName}_${selectedYear}.xlsx`,
      );
    } catch (error) {
      console.error("Excel download error:", error);
      handleApiErrorResponses(error);
    }
  };

  useEffect(() => {
    if (!startDate || !endDate) return;

    if (!filterStage?.includes("Paid")) {
      getMonthlyReport();
    }
  }, [startDate, endDate, filterStage]);
  useEffect(() => {
    const today = new Date();

    // Go to previous month
    const prevMonthDate = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      1,
    );

    // Start of previous month
    const startOfPrevMonth = new Date(
      prevMonthDate.getFullYear(),
      prevMonthDate.getMonth(),
      1,
    );

    // End of previous month (day 0 of current month)
    const endOfPrevMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      0,
      23,
      59,
      59,
      999,
    );

    setStartDate(startOfPrevMonth.toISOString());
    setEndDate(endOfPrevMonth.toISOString());

    // also preselect year/month in the dropdowns
    setSelectedYear(String(startOfPrevMonth.getFullYear()));
    setSelectedMonth(String(startOfPrevMonth.getMonth() + 1).padStart(2, "0"));
  }, []);

  useEffect(() => {
    if (stageType === "all") {
      const startIndex = 0;
      setFilteredReportData(reportData);
      const sliced = reportData.slice(startIndex, startIndex + limit);
      setPaginatedData(sliced);
    } else {
      const modifiedData = reportData?.filter((data) =>
        data?.currenthsaStage
          ?.toLowerCase()
          ?.includes(stageType?.toLowerCase()),
      );
      setFilteredReportData(modifiedData);
      setPage(1);
      const startIndex = 0;
      const sliced = modifiedData.slice(startIndex, startIndex + limit);
      setPaginatedData(sliced);
    }
  }, [stageType, reportData, limit]);

  useEffect(() => {
    if (caseType === "all") {
      const startIndex = 0;
      setFilteredReportData(reportData);
      const sliced = reportData.slice(startIndex, startIndex + limit);
      setPaginatedData(sliced);
    } else {
      const modifiedData = reportData?.filter((data) =>
        data?.caseType?.toLowerCase()?.includes(caseType?.toLowerCase()),
      );
      setFilteredReportData(modifiedData);
      setPage(1);
      const startIndex = 0;
      const sliced = modifiedData.slice(startIndex, startIndex + limit);
      setPaginatedData(sliced);
    }
  }, [caseType, reportData, limit]);

  useEffect(() => {
    if (searchKey === "" || searchKey === null) {
      const startIndex = 0;
      setFilteredReportData(reportData);
      const sliced = reportData.slice(startIndex, startIndex + limit);
      setPaginatedData(sliced);
    } else {
      const modifiedData = reportData?.filter((data) =>
        data?.patientName?.toLowerCase()?.includes(searchKey?.toLowerCase()),
      );
      const modifiedData2 = reportData?.filter((data) =>
        data?.pmjayRegistrationId
          ?.toLowerCase()
          ?.includes(searchKey?.toLowerCase()),
      );
      const merged = [...modifiedData, ...modifiedData2];
      const unique = Array.from(
        new Map(
          merged.map((item) => [item.pmjayRegistrationId, item]),
        ).values(),
      );

      setFilteredReportData(unique);
      setPage(1);
      const startIndex = 0;
      const sliced = unique.slice(startIndex, startIndex + limit);
      setPaginatedData(sliced);
    }
  }, [searchKey, reportData, limit]);

  useEffect(() => {
    const totalItems = filteredReportData?.length || 0;
    setTotalPages(Math.ceil(totalItems / limit) || 1);
    setPage(1);
  }, [filteredReportData, limit]);

  useEffect(() => {
    const startIndex = (page - 1) * limit;
    const sliced = filteredReportData.slice(startIndex, startIndex + limit);
    setPaginatedData(sliced);
  }, [page, limit, filteredReportData]);

  useEffect(() => {
    setLimit(value);
    setPage(1);
  }, [value]);

  // dynamic years from 2020 to current
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = 2020; y <= currentYear; y++) years.push(y);

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  return (
    <div>
      <div
        style={{ color: "rgba(0,0,0,0.5)" }}
        className="text-[11px] 2xl:text-sm leading-[18.27px] -tracking-[3%]"
      >
        Home / Monthly Case Report
      </div>

      <div className=" flex items-center justify-between mt-[24px] 2xl:mt-[30px]">
        <div
          style={{ background: "rgba(255,255,255,0.5)" }}
          className="w-fit text-[11px] 2xl:text-sm leading-[18.27px] -tracking-[3%] flex gap-[24px] 2xl:gap-[30px] items-center py-3 2xl:py-4 px-4 2xl:px-5  rounded-full"
        >
          <div className="flex items-center">
            <input
              type="radio"
              name="type"
              id="all"
              value={"all"}
              checked={stageType === "all"}
              onChange={(e) => {
                setStageType(e.target.value);
                setSubStageType("");
                setPage(1);
              }}
            />
            <label htmlFor="all" className="ml-[5px]">
              All
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="radio"
              name="type"
              id="admission"
              value={"Admission"}
              onChange={(e) => {
                setStageType(e.target.value);
                setPage(1);
              }}
            />
            <label htmlFor="admission" className="ml-[5px]">
              Admission
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="radio"
              name="type"
              id="enhancement"
              value={"Enhancement"}
              onChange={(e) => {
                setStageType(e.target.value);
                setPage(1);
              }}
            />
            <label htmlFor="enhancement" className="ml-[5px]">
              Enhancement
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="radio"
              name="type"
              id="claim"
              value={"Claim"}
              onChange={(e) => {
                setStageType(e.target.value);
                setPage(1);
              }}
            />
            <label htmlFor="claim" className="ml-[5px]">
              Discharge/Claim
            </label>
          </div>
        </div>

        <div
          style={{
            background: "rgba(255,255,255,0.5)",
            border: "1px solid #FFFFFF",
          }}
          className="flex justify-between items-center xl:w-[300px] 2xl:w-[500px] px-4 2xl:px-5 py-[12px] 2xl:py-[15px] rounded-full"
        >
          <input
            type="text"
            placeholder="SEARCH ANYTHING"
            className="w-[80%] text-[12px] 2xl:text-[16px] bg-transparent outline-none border-none"
            onChange={handleSearch}
          />
          <img
            src={SearchIcon}
            alt="search-icon"
            className="h-[14px] w-[18px] 2xl:h-[28px] 2xl:w-[23px]"
          />
        </div>
      </div>

      <div
        id="case-list"
        style={{ background: "rgba(255,255,255,0.5)" }}
        className="p-4 2xl:p-8 mt-4 rounded-[30px]"
      >
        <div className="w-full flex items-center justify-between ">
          <div className="text-[30px] 2xl:text-[50px] leading-[65.26px] -tracking-[7%]">
            Monthly Case Report
          </div>
          <div className="relative">
            {/* Download Button */}
            <div
              className="flex items-center gap-1 text-[#1B1B1B] text-sm px-4 py-[14px] border border-solid border-[#017C71] rounded-full cursor-pointer"
              onClick={() => {
                setShowDownloadMenu((prev) => !prev);
              }}
            >
              <FileDownloadOutlinedIcon />
              Download Report
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`transition-transform duration-200 ${
                  showDownloadMenu ? "rotate-180" : ""
                }`}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            {/* Download Dropdown */}
            {showDownloadMenu && (
              <div className="absolute right-0 top-full mt-0 w-full bg-white border border-[#AFAFAF] rounded-b-[10px] rounded-t-none overflow-hidden z-50 shadow-sm">
                <button
                  type="button"
                  onClick={() => {
                    setShowDownloadMenu(false);

                    if (filteredReportData?.length > 0) {
                      generatePdf(filteredReportData);
                    } else {
                      toast.info("No data present to generate pdf.");
                    }
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-[#191919] bg-white hover:bg-[#F8F8F8] border-none"
                >
                  Download PDF
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowDownloadMenu(false);

                    if (filteredReportData?.length > 0) {
                      generateExcel(filteredReportData);
                    } else {
                      toast.info("No data present to generate excel.");
                    }
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-[#191919] bg-white hover:bg-[#F8F8F8] border-none"
                >
                  Download Excel
                </button>
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            border: "1px solid #FFFFFF",
          }}
          className="mt-[32px] 2xl:mt-[40px] mb-[24px] 2xl:mb-[30px]"
        />

        <div className="flex items-end justify-between">
          <div className="flex items-end gap-[30px] ">
            {/* Filter Stage */}
            <div className="flex items-center gap-2 px-4 py-4 rounded-full border border-solid border-[#017C57] w-fit cursor-pointer">
              <select
                value={filterStage}
                onChange={(e) => {
                  setCaseStage(e.target.value);
                  setFilterStage(e.target.value);
                }}
                className="bg-transparent outline-none cursor-pointer appearance-none border-none pl-1 pr-6"
              >
                {/* <option value={"all"}>Select Case Stage</option> */}
                <option value={"Request Admission Initiated"}>
                  Admission Initiated
                </option>
                <option value={"Claim Documents Upload Pending"}>
                  Discharge Initiated
                </option>
                <option value={"Claim Initiated on TMS"}>
                  Claim Initiated on TMS
                </option>
                {/* <option value={"Claim Paid"}>Claim Paid</option> */}
                {/* {["Request Admission Initiated", "Claim Initiated on TMS"].map(
                  (num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  )
                )} */}
              </select>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="pointer-events-none -ml-4"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            {/* YEAR + MONTH */}
            <div className="flex gap-3">
              {/* Year */}
              <div className="flex items-center gap-2 px-4 py-4 rounded-full border border-solid border-[#017C57] w-fit cursor-pointer">
                <select
                  value={selectedYear}
                  onChange={(e) => {
                    setSelectedYear(e.target.value);
                    setPage(1);
                  }}
                  className="bg-transparent outline-none cursor-pointer appearance-none border-none pl-1 pr-6"
                >
                  <option value="">YEAR</option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="pointer-events-none -ml-4"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>

              {/* Month */}
              <div className="flex items-center gap-2 px-4 py-4 rounded-full border border-solid border-[#017C57] w-fit cursor-pointer">
                <select
                  value={selectedMonth}
                  onChange={(e) => {
                    setSelectedMonth(e.target.value);
                    setPage(1);
                  }}
                  className="bg-transparent outline-none cursor-pointer appearance-none border-none pl-1 pr-6"
                >
                  <option value="">MONTH</option>
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="pointer-events-none -ml-4"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>

            {/* Case Type */}
            {/* <div className="flex items-center gap-2 px-4 py-4 rounded-full border border-solid border-[#017C57] w-fit cursor-pointer">
              <select
                onChange={(e) => setCaseType(e.target.value)}
                className="bg-transparent outline-none cursor-pointer appearance-none border-none pl-1 pr-6 "
              >
                <option value={"all"} className="px-2">
                  Select Case Type
                </option>
                {["Conservative", "Surgical", "Daycare"].map((value) => (
                  <option key={value} value={value} className="px-2">
                    {value}
                  </option>
                ))}
              </select>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="pointer-events-none -ml-4"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div> */}
          </div>
        </div>

        <div className="bg-[white] p-[24px] 2xl:p-[30px] rounded-[15px] mt-[14px]">
          <div
            className={`grid ${filterStage?.includes("Paid") ? "grid-cols-[0.5fr_1.5fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_2fr]" : "grid-cols-[0.5fr_1.5fr_1fr_1fr_1.5fr_1fr_1fr_2fr]"} gap-2 text-[12px] 2xl:text-sm font-semibold 2xl:leading-[21.7px] -tracking-[3%]`}
          >
            <div> SNo.</div>
            <div>Patient Name</div>
            {!filterStage.includes("Paid") && <div>Beneficiary ID</div>}
            <div>Registered ID</div>
            {!filterStage?.includes("Paid") && <div>Gender/Age</div>}
            <div>Registered Date</div>
            <div>Admission Initiated Date</div>
            {filterStage?.includes("Paid") && <div>Claim Initiated Amount</div>}
            {filterStage?.includes("Paid") && <div>Claim Approved Amount</div>}
            {filterStage?.includes("Paid") && <div>Claim Paid Amount</div>}
            {filterStage?.includes("Paid") && <div>TDS</div>}
            {/* <div>Discharge Date</div> */}
            {/* <div>Claim Initiated Date</div> */}
            {/* <div>Case Type</div> */}
            <div className={`${filterStage?.includes("Paid") ? "" : ""}`}>
              Current Case Stage
            </div>
          </div>
          {loading ? (
            <div className="h-[200px] flex justify-center items-center">
              <CircularProgress />
            </div>
          ) : paginatedData?.length > 0 ? (
            paginatedData?.map((reportItem, idx) => {
              return (
                <div
                  key={idx}
                  style={{
                    background:
                      reportItem.currenthsaStage
                        ?.toLowerCase()
                        ?.includes("approved on tms") ||
                      reportItem.currenthsaStage
                        ?.toLowerCase()
                        ?.includes("claim paid")
                        ? "linear-gradient(90deg, #FFFFFF 0%, #B4FFCF 10.5%, #B4FFCF 88%, #FFFFFF 100%)"
                        : reportItem.currenthsaStage
                              ?.toLowerCase()
                              ?.includes("progress") ||
                            reportItem.currenthsaStage
                              ?.toLowerCase()
                              ?.includes("pending")
                          ? "linear-gradient(90deg, #FFFFFF 0%, rgba(237, 255, 140, 0.5) 13%, rgba(237, 255, 140, 0.5) 85%, #FFFFFF 100%)"
                          : "#FFFFFF",
                  }}
                  className={`grid ${filterStage?.includes("Paid") ? "grid-cols-[0.5fr_1.5fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_2fr]" : "grid-cols-[0.5fr_1.5fr_1fr_1fr_1.5fr_1fr_1fr_2fr]"} gap-2 text-[12px] 2xl:text-sm  py-1 items-center mt-[8px] 2xl:mt-[10px] first:mt-6 2xl:first:mt-8`}
                >
                  <div>{idx + 1}</div>
                  <div>
                    {reportItem?.patientName || "NA"}
                    {filterStage?.includes("Paid") && (
                      <div className="text-[11px] text-[#00000080] -tracking-[0.03em]">
                        {reportItem?.beneficiaryNo}
                      </div>
                    )}
                  </div>
                  {!filterStage?.includes("Paid") && (
                    <div>{reportItem?.beneficiaryNo || "NA"}</div>
                  )}
                  <div
                    className="cursor-text select-all text-ellipsis text-nowrap overflow-hidden"
                    title={reportItem?.pmjayRegistrationId}
                    onCopy={(e) => {
                      if (reportItem?.pmjayRegistrationId) {
                        e.preventDefault();
                        e.clipboardData.setData(
                          "text/plain",
                          reportItem.pmjayRegistrationId,
                        );
                      }
                    }}
                  >
                    {reportItem?.pmjayRegistrationId
                      ? reportItem.pmjayRegistrationId
                      : "NA"}
                  </div>
                  {!filterStage?.includes("Paid") && (
                    <div className=" whitespace-nowrap">
                      {reportItem?.gender
                        ? reportItem.gender.toLowerCase() === "male"
                          ? "M"
                          : reportItem.gender.toLowerCase() === "female"
                            ? "F"
                            : "O"
                        : "NA"}{" "}
                      /{reportItem?.age || "NA"}
                    </div>
                  )}
                  <div>
                    {reportItem?.registeredDate
                      ? new Date(reportItem?.registeredDate).toLocaleString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                          },
                        )
                      : "NA"}
                  </div>

                  <div className="">
                    {reportItem?.admissionStage?.[0]?.createdAt
                      ? new Date(
                          reportItem?.filterStage?.[0]?.createdAt,
                        ).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        })
                      : "NA"}
                  </div>
                  {/* <div>
                    {reportItem?.dischargeInitiatedDate
                      ? new Date(
                          reportItem?.dischargeInitiatedDate
                        ).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        })
                      : "NA"}
                  </div>
                  <div>
                    {reportItem?.claimInitiatedDate
                      ? new Date(reportItem?.claimInitiatedDate).toLocaleString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                          }
                        )
                      : "NA"}
                  </div> */}
                  {filterStage?.includes("Paid") && (
                    <>
                      {" "}
                      <div>
                        {reportItem?.caseDetails?.claimInitiatedAmount || "NA"}
                      </div>
                      <div>
                        {reportItem?.caseDetails?.claimApprovedAmount || "NA"}
                      </div>
                      <div>
                        {reportItem?.caseDetails?.claimPaidAmount || "NA"}
                      </div>
                      <div>{reportItem?.caseDetails?.tdsAmount || "NA"}</div>
                    </>
                  )}
                  <div className={` font-medium `}>
                    {reportItem?.currentStageHsaReportText || "NA"}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="h-[200px] flex justify-center items-center font-semibold text-xl">
              No Case Data Found
            </div>
          )}

          {filteredReportData?.length > 0 && (
            <div className="flex justify-center mt-[12px] 2xl:mt-[15px]">
              <HospitalCustomPaginaion
                page={page}
                setPage={setPage}
                totalPages={totalPages}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HsaMonthlyCaseReport;
